import React, { useState } from 'react';
import { ArrowLeft, CreditCard, Send, CheckCircle, AlertCircle } from 'lucide-react';

export default function RechargePage({ onBack, currentBalance }) {
  const [amount, setAmount] = useState('');
  const [requestSent, setRequestSent] = useState(false);
  const [error, setError] = useState('');

  const presetAmounts = [500, 1000, 2500, 5000, 10000, 25000];

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setAmount(value);
      setError('');
    }
  };

const handleSubmit = async () => {
  if (!amount || parseInt(amount) < 100) {
    setError('Le montant minimum est de 100 TND');
    return;
  }

  try {
    const token = localStorage.getItem('authToken');
    
    const response = await fetch('https://djebooking.com/api/recharge-requests', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        amount: parseFloat(amount),
        paymentMethod: 'Virement Bancaire', // Or let user choose
        paymentProof: null // Optional: add file upload later
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Erreur lors de l\'envoi');
    }

    setRequestSent(true);

    // Reset after 3 seconds
    setTimeout(() => {
      setRequestSent(false);
      setAmount('');
      onBack(); // Go back to home
    }, 3000);

  } catch (error) {
    setError(error.message || 'Erreur lors de l\'envoi de la demande');
  }
};


  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2B4979] via-[#3d5c8f] to-[#2B4979] py-12 px-4 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-green-400 rounded-full filter blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
      </div>

      <div className="container mx-auto max-w-2xl relative z-10">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-white hover:text-gray-200 transition mb-6"
        >
          <ArrowLeft size={20} />
          <span className="font-semibold">Retour</span>
        </button>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-8 text-white text-center">
            <div className="bg-white/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
              <CreditCard size={40} />
            </div>
            <h1 className="text-3xl font-bold mb-2">Recharger votre solde</h1>
            <p className="text-green-100">Solde actuel: <span className="font-bold text-xl">{currentBalance.toLocaleString()} TND</span></p>
          </div>

          {/* Content */}
          <div className="p-8">
            {!requestSent ? (
              <>
                {/* Amount Input */}
                <div className="mb-6">
                  <label className="block text-gray-700 font-semibold mb-3 text-lg">
                    Montant à recharger (TND)
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={amount}
                      onChange={handleAmountChange}
                      placeholder="Entrez le montant"
                      className={`w-full px-6 py-4 text-2xl font-bold border-2 rounded-xl focus:outline-none transition ${
                        error 
                          ? 'border-red-500 focus:border-red-600' 
                          : 'border-gray-300 focus:border-green-500'
                      }`}
                    />
                    <span className="absolute right-6 top-1/2 -translate-y-1/2 text-2xl font-bold text-gray-400">
                      TND
                    </span>
                  </div>
                  {error && (
                    <div className="flex items-center gap-2 mt-2 text-red-600">
                      <AlertCircle size={16} />
                      <span className="text-sm">{error}</span>
                    </div>
                  )}
                </div>

                {/* Preset Amounts */}
                <div className="mb-8">
                  <p className="text-gray-600 font-semibold mb-3">Montants suggérés:</p>
                  <div className="grid grid-cols-3 gap-3">
                    {presetAmounts.map((preset) => (
                      <button
                        key={preset}
                        onClick={() => {
                          setAmount(preset.toString());
                          setError('');
                        }}
                        className={`py-3 px-4 rounded-lg font-semibold transition transform hover:scale-105 ${
                          amount === preset.toString()
                            ? 'bg-green-500 text-white shadow-lg'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {preset.toLocaleString()} TND
                      </button>
                    ))}
                  </div>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Votre demande sera traitée par l'administration dans les plus brefs délais. 
                    Vous recevrez une notification une fois le rechargement effectué.
                  </p>
                </div>

                {/* Submit Button */}
                <button
                  onClick={handleSubmit}
                  disabled={!amount || parseInt(amount) < 100}
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-4 rounded-xl transition transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-xl flex items-center justify-center gap-3 text-lg"
                >
                  <Send size={24} />
                  Envoyer la demande
                </button>
              </>
            ) : (
              // Success Message
              <div className="text-center py-8 animate-fadeIn">
                <div className="bg-green-100 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
                  <CheckCircle size={48} className="text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-3">
                  Demande envoyée avec succès !
                </h2>
                <p className="text-gray-600 mb-2">
                  Votre demande de rechargement de <span className="font-bold text-green-600">{parseInt(amount).toLocaleString()} TND</span> a été envoyée.
                </p>
                <p className="text-sm text-gray-500">
                  L'administration vous contactera sous peu.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-6 text-center text-white/80 text-sm">
          <p>Pour toute question, contactez le support: <span className="font-semibold">support@gomax.tn</span></p>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
    </div>
  );
}